import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Input, PhoneInput, Button } from '@unified-checkout/design-system';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { ILoginForm } from '@app/dashboard/types/outerLayout/login';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { PROFILE } from '@app/dashboard/constants/profile';
import { useGlobalStore } from '@app/dashboard/store/global';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { login } from '@app/dashboard/services/outer_layout/login';
import { OUTER_ROUTES } from '@app/dashboard/routes/outer-routes';
import {
  required,
  isFormValid,
  phoneNumberValidation,
  validateField,
  isPhoneNumber
} from '@app/dashboard/utils/formValidations';
import { iso3ToIso2 } from '@app/dashboard/utils/countriesUtils';
import { handleAfterLogin } from '../utils';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { useInnerLayoutLogic } from '@app/dashboard/views/layout/InnerLayout/utils';
import styles from "../login.module.scss";

const usersTypes = [PROFILE.TYPE.MERCHANT, PROFILE.TYPE.MERCHANT_STAFF];

const FieldNames = {
  USER_NAME: "username",
  PASSWORD: "password",
};

const fieldsInitialState = {
  [FieldNames.USER_NAME]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: [required]
  },
  [FieldNames.PASSWORD]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: [required]
  }
};

const handlePhoneInputRender = (value: string) => {
  const renderPhoneInput = isPhoneNumber(value);

  return renderPhoneInput;
};

const SUBMIT_BTN_ID = 'login-form-submit-btn';

const LoginForm = (
  {
    setPhoneNumber,
    loading,
    setLoading,
    setCurrentStep,
    STEPS,
    redirectToRegister,
    setOtpToken
  }: ILoginForm
) => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useGlobalStore();
  const { handleSwitchDashboard } = useInnerLayoutLogic();
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const country = getCountryFromDomain();

  const { state } = useLocation();
  const { activatedUsername } = state || {};

  const [serverErrMsg, setServerErrMsg] = useState('');
  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [shouldRenderPhoneInput, setShouldRenderPhoneInput] = useState(handlePhoneInputRender(fields[FieldNames.USER_NAME].value as string));
  const [focusedElement, setFocusedElement] = useState<string | undefined>();

  const onChangeHandler = (fieldName: string, value: string) => {
    setServerErrMsg("");
    setFocusedElement(fieldName === FieldNames.USER_NAME ? FieldNames.USER_NAME : FieldNames.PASSWORD);

    const renderPhoneInput = handlePhoneInputRender(value);
    fieldName === FieldNames.USER_NAME && setShouldRenderPhoneInput(renderPhoneInput);

    setFields(prevState => {
      return ({
        ...prevState,
        [fieldName]: {
          ...prevState[fieldName],
          value: value,
          validations:
            renderPhoneInput && fieldName === FieldNames.USER_NAME ?
              [required, phoneNumberValidation(country)]
              :
              [required]
        }
      })
    });

    validateField(fields, setFields, fieldName, value);
  };

  useEffect(() => {
    activatedUsername && onChangeHandler(FieldNames.USER_NAME, activatedUsername as string);
  }, [activatedUsername]);

  useEffect(() => {
    const userName = fields[FieldNames.USER_NAME];

    userName.isValid !== undefined &&
      validateField(fields, setFields, FieldNames.USER_NAME, userName.value as string);
  }, [fields[FieldNames.USER_NAME].validations]);

  const handlePhoneChange = (number: string) => {
    const numWithoutPhoneCountryCode = number.replace(COUNTRIES_INFO[country].phone_country_code, '');

    onChangeHandler(FieldNames.USER_NAME, numWithoutPhoneCountryCode);
  };

  const handleForgetPassword = () => {
    navigate(OUTER_ROUTES.FORGET_PASSWORD);
  };

  const handleLogin = () => {
    setLoading(true);
    setServerErrMsg("");

    const args = {
      "userName": fields[FieldNames.USER_NAME].value as string,
      "password": fields[FieldNames.PASSWORD].value as string,
    };

    login(args)
      .then(async (response) => {
        const data = response?.data;
        // Check if OTP required
        if (data.hasOwnProperty("phone")) {
          const phone = data?.phone;
          const otpToken = data?.token;

          setPhoneNumber(phone);
          setOtpToken(otpToken);
          setCurrentStep(STEPS.OTP);
          setLoading(false);
        } else {
          const data = response?.data;
          const accessToken = data.access;
          const type = data?.profile.profile_type;
          const { acq_partner } = data.profile;
          if (!acq_partner && usersTypes.includes(type)) {
            await handleAfterLogin(response, navigate, setServerErrMsg);
            setIsAuthenticated(true);
          } else {
            // any user rather than Merchant or MerchantStaff
            await handleSwitchDashboard(accessToken);
            setLoading(false);
          }
        }
      })
      .catch((error) => {
        const status = error.response?.status;
        const msg = error.response?.data?.message;
        setLoading(false);

        // setServerErrMsg(status === 401 ? t('outerLayout.form.login.errors.wrongCredentials') : t('errors.somethingWentWrong'));
        setServerErrMsg(msg || t('errors.somethingWentWrong'));
      })
  };

  return (
    <>
      {
        shouldRenderPhoneInput ?
          <PhoneInput
            id={FieldNames.USER_NAME}
            name={FieldNames.USER_NAME}
            label={t('outerLayout.form.labels.emailOrUsernameOrPhone')}
            disableSelectCountry
            autoFocus={focusedElement === FieldNames.USER_NAME}
            phoneNumber={fields[FieldNames.USER_NAME].value}
            onChangeHandler={handlePhoneChange}
            country={iso3ToIso2(country)}
            error={fields[FieldNames.USER_NAME].errorMsg}
            disabled={loading}
          />
          :
          <Input
            id={FieldNames.USER_NAME}
            name={FieldNames.USER_NAME}
            type='text'
            label={t('outerLayout.form.labels.emailOrUsernameOrPhone')}
            value={fields[FieldNames.USER_NAME].value}
            required
            autoFocus={focusedElement === FieldNames.USER_NAME}
            onChange={(e) => { onChangeHandler(FieldNames.USER_NAME, e.target.value) }}
            status={fields[FieldNames.USER_NAME].isValid === false ? 'error' : 'default'}
            error={fields[FieldNames.USER_NAME].errorMsg}
            disabled={loading}
          />
      }

      <Input
        id={FieldNames.PASSWORD}
        name={FieldNames.PASSWORD}
        type='password'
        label={t('outerLayout.form.labels.password')}
        value={fields[FieldNames.PASSWORD].value}
        required
        onChange={(e) => { onChangeHandler(FieldNames.PASSWORD, e.target.value) }}
        forgetPasswordHandler={handleForgetPassword}
        forgetPasswordText={t('outerLayout.form.login.forgotPassword')}
        status={fields[FieldNames.PASSWORD].isValid === false ? 'error' : 'default'}
        error={fields[FieldNames.PASSWORD].errorMsg}
        autoFocus={focusedElement === FieldNames.PASSWORD}
        disabled={loading}
      />
      <div className='form__submit'>
        {serverErrMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}
        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.login.signIn')}
          loading={loading}
          disabled={!isFormValid(fields) || loading}
          onClickHandler={handleLogin}
        />
      </div>

      <div className={styles["login-form__dont-have-account"]}>
        <span className={styles["login-form__dont-have-account__text"]}>{t('outerLayout.form.dontHaveAcc')}</span>
        <div className={styles["login-form__dont-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.signUp')}
            onClickHandler={redirectToRegister}
          />
        </div>
      </div>
    </>
  )
}

export default LoginForm;